import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDCard from "components/MDCard";
import TextError from "utils/TextError";

import Modal from "@mui/material/Modal";
import Grid from "@mui/material/Grid";
import Collapse from "@mui/material/Collapse";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Icon from "@mui/material/Icon";

import { CSVLink } from "react-csv";


import {
  createTensileTest,
  createHardnessTest,
  createNotchImpactTest,
  createThermalStabilityTest,
  getTensileTest,
  getHardnessTest,
  getNotchImpactTest,
  getThermalStabilityTest
} from "../../../../store";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: 500, lg: 800 },
  height: "fit-content",
  maxHeight: "90%",
  boxShadow: 24,
  p: 0,
  pt: 2,
  overflow: "auto", 
};

const testTypeOptions = [
  { label: "Tensile Test", value: "tensile" },
  { label: "Hardness Test", value: "hardness" },
  { label: "Notch Impact Test", value: "notchImpact" },
  { label: "Thermal Stability Test", value: "thermalStability" },
];

const passFailOptions = [
  { label: "Pass", value: 1 },
  { label: "Fail", value: 0 },
];

const overchargeUnitOptions = [
  { label: "V/cell", value: "V/cell" },
];

const CreateTestRecordModal = ({
  open,
  handleClose,
  jwtToken,
  userId,
  dppOptions,
  createTensileTest,
  createHardnessTest,
  createNotchImpactTest,
  createThermalStabilityTest,
  onSuccessRefresh,
  getTensileTest,
  getHardnessTest,
  getNotchImpactTest,
  getThermalStabilityTest,
  dppIdSelectedForGlobalFilter,
}) => {
  const [selectedTest, setSelectedTest] = useState(null);

  const [allocateButtonName, setAllocateButtonName] = useState("Create");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (isSuccess) setTimeout(() => setIsSuccess(false), 3000);
    if (isError) setTimeout(() => setIsError(false), 3000);
  }, [isSuccess, isError]);







  const initialValues = {
    DPPID: "",
    // Tensile
    notchimpactStrengthMean: "",
    yeildProofStrengthMean: "",
    tensileStrengthMean: "",
    testTemperature: "",
    sampleShapeLocationOfSample: "",
    directionOfSample: "",

    // Hardness
    hardnessMean: "",
    hardnessIndividualValues: "",
    locationOfSample: "",
    overchargeTest: null,
    overchargeTestValue: "",
    overchargeTestUnit: null,
    shortCircuitTest: null,
    shortCircuitTestValue: "",
    shortCircuitTestUnit: "milliohm",

    // Notch Impact
    notchImpactStrengthMean: "",
    notchImpactStrengthIndividualValues: "",
    notchImpactWorkMean: "",
    smapleWidth: "",
    sampleType: "",

    // --- NEW: Thermal Stability ---
    minOperatingTemp: "",
    maxOperatingTemp: "",
    thermalStabilityTestTemp: "",
    thermalExposureDuration: "",
    thermalExposureDurationUnit: "Hours",
    thermalRunawayOnsetTemp: "",
    temperatureRise: "",
    operatingTempUnit: "C",
    testResult: null,
    remark: "",
  };

  const validationSchema = useMemo(() => {
    let baseSchema = {
      DPPID: Yup.object().required("Select DPP Code"),
    };

    if (!selectedTest) {
      return Yup.object({ ...baseSchema });
    }

    if (selectedTest.value === "tensile") {
      return Yup.object({
        ...baseSchema,
        notchimpactStrengthMean: Yup.number().typeError("Must be a number").required("Required"),
        yeildProofStrengthMean: Yup.number().typeError("Must be a number").required("Required"),
        tensileStrengthMean: Yup.number().typeError("Must be a number").required("Required"),
        testTemperature: Yup.number().typeError("Must be a number").required("Required"),
        sampleShapeLocationOfSample: Yup.string().required("Required"),
        directionOfSample: Yup.string().required("Required"),
      });
    } else if (selectedTest.value === "hardness") {
      return Yup.object({
        ...baseSchema,
        notchimpactStrengthMean: Yup.number().typeError("Must be a number").required("Required"),
        hardnessMean: Yup.number().typeError("Must be a number").required("Required"),
        testTemperature: Yup.number().typeError("Must be a number").required("Required"),
        hardnessIndividualValues: Yup.number().typeError("Must be a number").required("Required"),
        locationOfSample: Yup.string().required("Required"),
        directionOfSample: Yup.string().required("Required"),

        overchargeTest: Yup.object().nullable(),
        // .required("Required"),
        overchargeTestValue: Yup.number().typeError("Must be a number"),
        // .required("Required"),
        overchargeTestUnit: Yup.object().nullable(),
        // .required("Required"),
        shortCircuitTest: Yup.object().nullable(),
        // .required("Required"),
        shortCircuitTestValue: Yup.number().typeError("Must be a number"),
        // .required("Required"),
        shortCircuitTestUnit: Yup.string(),
        // .required("Required"),
      });
    } else if (selectedTest.value === "notchImpact") {
      return Yup.object({
        ...baseSchema,
        notchImpactStrengthMean: Yup.number().typeError("Must be a number").required("Required"),
        notchImpactStrengthIndividualValues: Yup.number().typeError("Must be a number").required("Required"),
        notchImpactWorkMean: Yup.number().typeError("Must be a number").required("Required"),
        smapleWidth: Yup.number().typeError("Must be a number").required("Required"),
        sampleType: Yup.string().required("Required"),
      });
    } else if (selectedTest.value === "thermalStability") {
      return Yup.object({
        ...baseSchema,
        minOperatingTemp: Yup.number().typeError("Must be a number").required("Required"),
        maxOperatingTemp: Yup.number().typeError("Must be a number").required("Required"),
        thermalStabilityTestTemp: Yup.number().typeError("Must be a number").required("Required"),
        thermalExposureDuration: Yup.number().typeError("Must be a number").required("Required"),
        thermalExposureDurationUnit: Yup.string().required("Required"),
        thermalRunawayOnsetTemp: Yup.number().typeError("Must be a number").required("Required"),
        temperatureRise: Yup.number().typeError("Must be a number").required("Required"),
        operatingTempUnit: Yup.string().required("Required"),
        testResult: Yup.object().nullable().required("Required"),
        remark: Yup.string().required("Required"),
      });
    }
  }, [selectedTest]);

  const onSubmit = async (values, { resetForm }) => {
    if (!selectedTest) {
      setErrorMsg("Please select a test module first.");
      setIsError(true);
      return;
    }

    setAllocateButtonName("Please wait...");
    let res;

    if (selectedTest.value === "tensile") {
      const payload = {
        dppId: values.DPPID?.value,
        notchimpactStrengthMean: String(values.notchimpactStrengthMean),
        yeildProofStrengthMean: String(values.yeildProofStrengthMean),
        tensileStrengthMean: String(values.tensileStrengthMean),
        testTemperature: String(values.testTemperature),
        sampleShapeLocationOfSample: values.sampleShapeLocationOfSample,
        directionOfSample: values.directionOfSample,
        cdBy: userId || "system",
      };
      res = await createTensileTest(payload, jwtToken);
    } else if (selectedTest.value === "hardness") {
      const payload = {
        dppId: values.DPPID?.value,
        notchimpactStrengthMean: String(values.notchimpactStrengthMean),
        hardnessMean: String(values.hardnessMean),
        testTemperature: String(values.testTemperature),
        hardnessIndividualValues: String(values.hardnessIndividualValues),
        locationOfSample: values.locationOfSample,
        directionOfSample: values.directionOfSample,

        overchargeTest: values.overchargeTest?.value,
        overchargeTestValue: String(values.overchargeTestValue),
        overchargeTestUnit: values.overchargeTestUnit?.value,
        shortCircuitTest: values.shortCircuitTest?.value,
        shortCircuitTestValue: String(values.shortCircuitTestValue),
        shortCircuitTestUnit: values.shortCircuitTestUnit,

        cdBy: userId || "system",
      };
      res = await createHardnessTest(payload, jwtToken);
    } else if (selectedTest.value === "notchImpact") {
      const payload = {
        dppId: values.DPPID?.value,
        notchImpactStrengthMean: String(values.notchImpactStrengthMean),
        notchImpactStrengthIndividualValues: String(values.notchImpactStrengthIndividualValues),
        notchImpactWorkMean: String(values.notchImpactWorkMean),
        smapleWidth: String(values.smapleWidth),
        sampleType: values.sampleType,
        cdBy: userId || "system",
      };
      res = await createNotchImpactTest(payload, jwtToken);
    } else if (selectedTest.value === "thermalStability") {
      const payload = {
        dppId: values.DPPID?.value,
        minOperatingTemp: String(values.minOperatingTemp),
        maxOperatingTemp: String(values.maxOperatingTemp),
        thermalStabilityTestTemp: String(values.thermalStabilityTestTemp),
        thermalExposureDuration: String(values.thermalExposureDuration),
        thermalExposureDurationUnit: values.thermalExposureDurationUnit,
        thermalRunawayOnsetTemp: String(values.thermalRunawayOnsetTemp),
        temperatureRise: String(values.temperatureRise),
        operatingTempUnit: values.operatingTempUnit,
        testResult: values.testResult?.value,
        remark: values.remark,
        userId: userId || "system",
      };
      res = await createThermalStabilityTest(payload, jwtToken);
    }

    if (res.status) {
      setIsSuccess(true);

      let filterId = dppIdSelectedForGlobalFilter?.value || null;

      if (selectedTest.value === "tensile") {
        getTensileTest(filterId, jwtToken);
      } else if (selectedTest.value === "hardness") {
        getHardnessTest(filterId, jwtToken);
      } else if (selectedTest.value === "notchImpact") {
        getNotchImpactTest(filterId, jwtToken);
      } else if (selectedTest.value === "thermalStability") {
        getThermalStabilityTest(filterId, jwtToken);
      }

      if (onSuccessRefresh) onSuccessRefresh(selectedTest.value);

      setTimeout(() => {
        setAllocateButtonName("Create");
        // resetForm();
        setSelectedTest(null);
        handleClose();
      }, 2000);
    } else {
      setErrorMsg(res?.data?.msg || "Something went wrong");
      setIsError(true);
      setAllocateButtonName("Create");
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit,
  });

  const handleModalClose = (event, reason) => {
    if (reason === "backdropClick")
      return;
    formik.resetForm();
    setSelectedTest(null);
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleModalClose}>
      <MDCard sx={style}>
        <MDBox ml={3} mt={-1} display="flex" alignItems="center">
          <MDTypography variant="h5" fontWeight="medium" flexGrow={1}>
            Create Test Record
          </MDTypography>
          <Tooltip title="Close">
            <IconButton
              style={{
                background: "whitesmoke",
                color: "black",
                borderRadius: 5,
                marginRight: "10px",
                marginTop: "4px",
              }}
              onClick={handleModalClose}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>

          
        </MDBox>
        
        <hr style={{ marginTop: "0.5rem", marginBottom: "0rem" }} />

        <MDBox pt={1} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>

              {/* DPP Code */}
              <Grid item xs={12} lg={6}>
                <MDBox mb={1} ml={1}>
                  <MDTypography variant="h6" style={{ fontWeight: "500" }}>
                    DPP Code
                  </MDTypography>
                  <Select
                    isClearable
                    className="select-css"
                    placeholder="Choose one..."
                    name="DPPID"
                    value={formik.values.DPPID}
                    options={dppOptions}
                    onBlur={() => formik.handleBlur({ target: { name: "DPPID" } })}
                    onChange={(selectedOption) => {
                      formik.setFieldValue("DPPID", selectedOption);
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  {formik.touched.DPPID && formik.errors.DPPID && (
                    <TextError msg={formik.errors.DPPID} />
                  )}
                </MDBox>
              </Grid>

              <Grid item xs={12} lg={6}>
                <MDBox mb={1} ml={1}>
                  <MDTypography variant="h6" style={{ fontWeight: "500" }}>
                    Select Test Module
                  </MDTypography>
                  <Select
                    className="select-css"
                    placeholder="Choose test type..."
                    options={testTypeOptions}
                    value={selectedTest}
                    onChange={(option) => {
                      setSelectedTest(option);
                      formik.setTouched({}); // Clear errors on test switch
                    }}
                    isClearable={true}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </MDBox>
              </Grid>

              {/* ===================== CONDITIONAL RENDERING ===================== */}

              {/* TENSILE FIELDS */}
              {selectedTest?.value === "tensile" && (
                <>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">
                        Notch Impact Strength(Mean)(kJ/m²)
                      </MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="notchimpactStrengthMean"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.notchimpactStrengthMean}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.notchimpactStrengthMean &&
                          Boolean(formik.errors.notchimpactStrengthMean)
                        }
                      />
                      {formik.touched.notchimpactStrengthMean && formik.errors.notchimpactStrengthMean && (
                        <TextError msg={formik.errors.notchimpactStrengthMean} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">
                        Yield Proof Strength (Mean)(N/mm²)
                      </MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="yeildProofStrengthMean"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.yeildProofStrengthMean}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.yeildProofStrengthMean &&
                          Boolean(formik.errors.yeildProofStrengthMean)
                        }
                      />
                      {formik.touched.yeildProofStrengthMean && formik.errors.yeildProofStrengthMean && (
                        <TextError msg={formik.errors.yeildProofStrengthMean} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">
                        Tensile Strength (Mean)(MPa)
                      </MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="tensileStrengthMean"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.tensileStrengthMean}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.tensileStrengthMean &&
                          Boolean(formik.errors.tensileStrengthMean)
                        }
                      />
                      {formik.touched.tensileStrengthMean && formik.errors.tensileStrengthMean && (
                        <TextError msg={formik.errors.tensileStrengthMean} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Test Temperature(°C)</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="testTemperature"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.testTemperature}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.testTemperature &&
                          Boolean(formik.errors.testTemperature)
                        }
                      />
                      {formik.touched.testTemperature && formik.errors.testTemperature && (
                        <TextError msg={formik.errors.testTemperature} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">
                        Location of Sample
                      </MDTypography>
                      <MDInput
                        size="small"
                        type="text"
                        name="sampleShapeLocationOfSample"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.sampleShapeLocationOfSample}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.sampleShapeLocationOfSample &&
                          Boolean(formik.errors.sampleShapeLocationOfSample)
                        }
                      />
                      {formik.touched.sampleShapeLocationOfSample && formik.errors.sampleShapeLocationOfSample && (
                        <TextError msg={formik.errors.sampleShapeLocationOfSample} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">
                        Direction of Sample
                      </MDTypography>
                      <MDInput
                        size="small"
                        type="text"
                        name="directionOfSample"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.directionOfSample}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.directionOfSample &&
                          Boolean(formik.errors.directionOfSample)
                        }
                      />
                      {formik.touched.directionOfSample && formik.errors.directionOfSample && (
                        <TextError msg={formik.errors.directionOfSample} />
                      )}
                    </MDBox>
                  </Grid>
                </>
              )}

              {/* HARDNESS FIELDS */}
              {selectedTest?.value === "hardness" && (
                <>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">
                        Notch Impact Strength(Mean)(kJ/m²)
                      </MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="notchimpactStrengthMean"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.notchimpactStrengthMean}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.notchimpactStrengthMean && Boolean(formik.errors.notchimpactStrengthMean)}
                      />
                      {formik.touched.notchimpactStrengthMean && formik.errors.notchimpactStrengthMean && (
                        <TextError msg={formik.errors.notchimpactStrengthMean} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Hardness (Mean)(HRC)</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="hardnessMean"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.hardnessMean}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.hardnessMean && Boolean(formik.errors.hardnessMean)}
                      />
                      {formik.touched.hardnessMean && formik.errors.hardnessMean && (
                        <TextError msg={formik.errors.hardnessMean} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Test Temperature(°C)</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="testTemperature"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.testTemperature}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.testTemperature && Boolean(formik.errors.testTemperature)}
                      />
                      {formik.touched.testTemperature && formik.errors.testTemperature && (
                        <TextError msg={formik.errors.testTemperature} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Individual Values</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="hardnessIndividualValues"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.hardnessIndividualValues}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.hardnessIndividualValues && Boolean(formik.errors.hardnessIndividualValues)}
                      />
                      {formik.touched.hardnessIndividualValues && formik.errors.hardnessIndividualValues && (
                        <TextError msg={formik.errors.hardnessIndividualValues} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Location of Sample</MDTypography>
                      <MDInput
                        size="small"
                        type="text"
                        name="locationOfSample"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.locationOfSample}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.locationOfSample && Boolean(formik.errors.locationOfSample)}
                      />
                      {formik.touched.locationOfSample && formik.errors.locationOfSample && (
                        <TextError msg={formik.errors.locationOfSample} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Direction of Sample</MDTypography>
                      <MDInput
                        size="small"
                        type="text"
                        name="directionOfSample"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.directionOfSample}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.directionOfSample && Boolean(formik.errors.directionOfSample)}
                      />
                      {formik.touched.directionOfSample && formik.errors.directionOfSample && (
                        <TextError msg={formik.errors.directionOfSample} />
                      )}
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Overcharge Test</MDTypography>
                      <Select
                        isClearable
                        className="select-css"
                        placeholder="Pass/Fail..."
                        name="overchargeTest"
                        value={formik.values.overchargeTest}
                        options={passFailOptions}
                        onChange={(option) => formik.setFieldValue("overchargeTest", option)}
                        onBlur={() => formik.setFieldTouched("overchargeTest", true)}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                      {formik.touched.overchargeTest && formik.errors.overchargeTest && (
                        <TextError msg={formik.errors.overchargeTest} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Overcharge Test Value</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="overchargeTestValue"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.overchargeTestValue}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.overchargeTestValue && Boolean(formik.errors.overchargeTestValue)}
                      />
                      {formik.touched.overchargeTestValue && formik.errors.overchargeTestValue && (
                        <TextError msg={formik.errors.overchargeTestValue} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Overcharge Test Unit</MDTypography>
                      <Select
                        isClearable
                        className="select-css"
                        placeholder="Select Unit..."
                        name="overchargeTestUnit"
                        value={formik.values.overchargeTestUnit}
                        options={overchargeUnitOptions}
                        onChange={(option) => formik.setFieldValue("overchargeTestUnit", option)}
                        onBlur={() => formik.setFieldTouched("overchargeTestUnit", true)}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                      {formik.touched.overchargeTestUnit && formik.errors.overchargeTestUnit && (
                        <TextError msg={formik.errors.overchargeTestUnit} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Short Circuit Test</MDTypography>
                      <Select
                        isClearable
                        className="select-css"
                        placeholder="Pass/Fail..."
                        name="shortCircuitTest"
                        value={formik.values.shortCircuitTest}
                        options={passFailOptions}
                        onChange={(option) => formik.setFieldValue("shortCircuitTest", option)}
                        onBlur={() => formik.setFieldTouched("shortCircuitTest", true)}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                      {formik.touched.shortCircuitTest && formik.errors.shortCircuitTest && (
                        <TextError msg={formik.errors.shortCircuitTest} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Short Circuit Test Value</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="shortCircuitTestValue"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.shortCircuitTestValue}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.shortCircuitTestValue && Boolean(formik.errors.shortCircuitTestValue)}
                      />
                      {formik.touched.shortCircuitTestValue && formik.errors.shortCircuitTestValue && (
                        <TextError msg={formik.errors.shortCircuitTestValue} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Short Circuit Test Unit</MDTypography>
                      <MDInput
                        size="small"
                        type="text"
                        name="shortCircuitTestUnit"
                        fullWidth
                        value={formik.values.shortCircuitTestUnit}
                        onChange={formik.handleChange}
                        disabled={true}
                      />
                    </MDBox>
                  </Grid>
                </>
              )}

              {/* NOTCH IMPACT FIELDS */}
              {selectedTest?.value === "notchImpact" && (
                <>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Strength Mean(kJ/m²)</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="notchImpactStrengthMean"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.notchImpactStrengthMean}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.notchImpactStrengthMean && Boolean(formik.errors.notchImpactStrengthMean)}
                      />
                      {formik.touched.notchImpactStrengthMean && formik.errors.notchImpactStrengthMean && (
                        <TextError msg={formik.errors.notchImpactStrengthMean} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Strength Indv Values</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="notchImpactStrengthIndividualValues"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.notchImpactStrengthIndividualValues}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.notchImpactStrengthIndividualValues && Boolean(formik.errors.notchImpactStrengthIndividualValues)}
                      />
                      {formik.touched.notchImpactStrengthIndividualValues && formik.errors.notchImpactStrengthIndividualValues && (
                        <TextError msg={formik.errors.notchImpactStrengthIndividualValues} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Work Mean(kJ/m²)</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="notchImpactWorkMean"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.notchImpactWorkMean}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.notchImpactWorkMean && Boolean(formik.errors.notchImpactWorkMean)}
                      />
                      {formik.touched.notchImpactWorkMean && formik.errors.notchImpactWorkMean && (
                        <TextError msg={formik.errors.notchImpactWorkMean} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Sample Width</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="smapleWidth"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.smapleWidth}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.smapleWidth && Boolean(formik.errors.smapleWidth)}
                      />
                      {formik.touched.smapleWidth && formik.errors.smapleWidth && (
                        <TextError msg={formik.errors.smapleWidth} />
                      )}
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Sample Type</MDTypography>
                      <MDInput
                        size="small"
                        type="text"
                        name="sampleType"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.sampleType}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.sampleType && Boolean(formik.errors.sampleType)}
                      />
                      {formik.touched.sampleType && formik.errors.sampleType && (
                        <TextError msg={formik.errors.sampleType} />
                      )}
                    </MDBox>
                  </Grid>
                </>
              )}

              {/* --- NEW: THERMAL STABILITY FIELDS --- */}
              {selectedTest?.value === "thermalStability" && (
                <>
                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Min Operating Temp</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="minOperatingTemp"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.minOperatingTemp}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.minOperatingTemp && Boolean(formik.errors.minOperatingTemp)}
                      />
                      {formik.touched.minOperatingTemp && formik.errors.minOperatingTemp && (
                        <TextError msg={formik.errors.minOperatingTemp} />
                      )}
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Max Operating Temp</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="maxOperatingTemp"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.maxOperatingTemp}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.maxOperatingTemp && Boolean(formik.errors.maxOperatingTemp)}
                      />
                      {formik.touched.maxOperatingTemp && formik.errors.maxOperatingTemp && (
                        <TextError msg={formik.errors.maxOperatingTemp} />
                      )}
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Stability Test Temp</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="thermalStabilityTestTemp"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.thermalStabilityTestTemp}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.thermalStabilityTestTemp && Boolean(formik.errors.thermalStabilityTestTemp)}
                      />
                      {formik.touched.thermalStabilityTestTemp && formik.errors.thermalStabilityTestTemp && (
                        <TextError msg={formik.errors.thermalStabilityTestTemp} />
                      )}
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Exposure Duration</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="thermalExposureDuration"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.thermalExposureDuration}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.thermalExposureDuration && Boolean(formik.errors.thermalExposureDuration)}
                      />
                      {formik.touched.thermalExposureDuration && formik.errors.thermalExposureDuration && (
                        <TextError msg={formik.errors.thermalExposureDuration} />
                      )}
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Exposure Duration Unit</MDTypography>
                      <MDInput
                        size="small"
                        type="text"
                        name="thermalExposureDurationUnit"
                        fullWidth
                        value={formik.values.thermalExposureDurationUnit}
                        onChange={formik.handleChange}
                        disabled={false} // Assuming it can be typed, or you can disable it if fixed
                      />
                      {formik.touched.thermalExposureDurationUnit && formik.errors.thermalExposureDurationUnit && (
                        <TextError msg={formik.errors.thermalExposureDurationUnit} />
                      )}
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Runaway Onset Temp</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="thermalRunawayOnsetTemp"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.thermalRunawayOnsetTemp}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.thermalRunawayOnsetTemp && Boolean(formik.errors.thermalRunawayOnsetTemp)}
                      />
                      {formik.touched.thermalRunawayOnsetTemp && formik.errors.thermalRunawayOnsetTemp && (
                        <TextError msg={formik.errors.thermalRunawayOnsetTemp} />
                      )}
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Temperature Rise</MDTypography>
                      <MDInput
                        size="small"
                        type="number"
                        name="temperatureRise"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.temperatureRise}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.temperatureRise && Boolean(formik.errors.temperatureRise)}
                      />
                      {formik.touched.temperatureRise && formik.errors.temperatureRise && (
                        <TextError msg={formik.errors.temperatureRise} />
                      )}
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Operating Temp Unit</MDTypography>
                      <MDInput
                        size="small"
                        type="text"
                        name="operatingTempUnit"
                        fullWidth
                        value={formik.values.operatingTempUnit}
                        onChange={formik.handleChange}
                        disabled={false} // Or true if fixed to 'C'
                      />
                      {formik.touched.operatingTempUnit && formik.errors.operatingTempUnit && (
                        <TextError msg={formik.errors.operatingTempUnit} />
                      )}
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Test Result</MDTypography>
                      <Select
                        isClearable
                        className="select-css"
                        placeholder="Pass/Fail..."
                        name="testResult"
                        value={formik.values.testResult}
                        options={passFailOptions}
                        onChange={(option) => formik.setFieldValue("testResult", option)}
                        onBlur={() => formik.setFieldTouched("testResult", true)}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                      {formik.touched.testResult && formik.errors.testResult && (
                        <TextError msg={formik.errors.testResult} />
                      )}
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <MDBox mb={1} ml={1}>
                      <MDTypography variant="h6">Remark</MDTypography>
                      <MDInput
                        size="small"
                        type="text"
                        name="remark"
                        fullWidth
                        autoComplete="off"
                        value={formik.values.remark}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.remark && Boolean(formik.errors.remark)}
                      />
                      {formik.touched.remark && formik.errors.remark && (
                        <TextError msg={formik.errors.remark} />
                      )}
                    </MDBox>
                  </Grid>
                </>
              )}

              {/* Status & Submit Button */}
              <Grid item xs={12}>
                <Collapse in={isError}>
                  <Alert
                    severity="error"
                    action={
                      <IconButton
                        size="small"
                        onClick={() => setIsError(false)}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    }
                  >
                    {errorMsg}
                  </Alert>
                </Collapse>
                <Collapse in={isSuccess}>
                  <Alert
                    severity="success"
                    action={
                      <IconButton
                        size="small"
                        onClick={() => setIsSuccess(false)}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    }
                  >
                    Record Created Successfully!
                  </Alert>
                </Collapse>
                <MDBox mt={2}>
                  <MDButton
                    color="info"
                    type="submit"
                    fullWidth
                    disabled={allocateButtonName === "Please wait..."}
                  >
                    {allocateButtonName}
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
      </MDCard>
    </Modal>
  );
};

const mapDispatchToProps = {
  createTensileTest,
  createHardnessTest,
  createNotchImpactTest,
  createThermalStabilityTest, // Added
  getTensileTest,
  getHardnessTest,
  getNotchImpactTest,
  getThermalStabilityTest // Added
};

export default connect(null, mapDispatchToProps)(CreateTestRecordModal);